import { Link, NavLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSession } from '../lib/auth-client';
import { useUIStore } from '../store/ui';
import { Button } from '@img/ui';
import { cn } from '@img/ui';
import { BrandLogo } from '../components/BrandLogo';
import { MobileBottomNav } from '../components/MobileBottomNav';
import { RamhCredit } from '../components/RamhCredit';
import { NotificationsBell } from '../components/NotificationsBell';
import { UserMenu, type UserMenuUser } from '../components/UserMenu';

export function PublicLayout() {
  const { t, i18n } = useTranslation();
  const { data: session } = useSession();
  const { mobileNavOpen, setMobileNavOpen } = useUIStore();
  const setLanguage = useUIStore((s) => s.setLanguage);

  const toggleLang = () => {
    const next = i18n.language === 'ar' ? 'en' : 'ar';
    i18n.changeLanguage(next);
    setLanguage(next);
  };

  const navItems = [
    { to: '/',            label: t('nav.home') },
    { to: '/properties',  label: t('nav.properties') },
    { to: '/auctions',    label: t('nav.auctions') },
    { to: '/map',         label: t('nav.map') },
    { to: '/calculators', label: t('nav.calculators') },
    { to: '/design',      label: t('nav.design') },
    { to: '/mullak',      label: t('nav.mullak') },
    { to: '/brokers',     label: t('nav.brokers') },
    { to: '/contact',     label: t('nav.contact') },
    { to: '/about',       label: t('nav.about') },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-light)]">
      {/* 🎯 Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          {/* Logo — يصغر على الشاشات المتوسطة */}
          <Link to="/" className="shrink-0">
            <span className="hidden sm:block"><BrandLogo size="sm" showText={true} /></span>
            <span className="block sm:hidden"><BrandLogo size="sm" showText={false} /></span>
          </Link>

          {/* Desktop Nav — جنب اللوقو على اليمين، حجم مدمج عشان يبقى فراغ على اليسار */}
          <nav className="hidden xl:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  cn(
                    'px-2 py-1 rounded-lg text-[11px] font-bold transition border whitespace-nowrap',
                    isActive
                      ? 'bg-navy-dark text-white border-navy-dark shadow-sm'
                      : 'bg-white text-navy-dark border-gray-200 hover:border-navy-dark hover:bg-navy-dark hover:text-white',
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Actions — ms-auto يدفعها لأقصى اليسار */}
          <div className="flex items-center gap-1.5 shrink-0 ms-auto">
            <Button variant="ghost" size="sm" onClick={toggleLang}>
              {i18n.language === 'ar' ? 'EN' : 'ع'}
            </Button>
            {session?.user && <NotificationsBell />}
            {session?.user ? (
              <UserMenu user={session.user as UserMenuUser} variant="public" />
            ) : (
              <>
                <Link to="/login" className="hidden sm:block">
                  <Button variant="ghost" size="sm">{t('nav.login')}</Button>
                </Link>
                <Link to="/register" className="hidden md:block">
                  <Button variant="gold" size="sm">{t('nav.register')}</Button>
                </Link>
              </>
            )}
            {/* Mobile burger — يظهر دون XL */}
            <button
              onClick={() => setMobileNavOpen(!mobileNavOpen)}
              className="xl:hidden p-2 rounded-lg hover:bg-gray-100"
              aria-label="menu"
            >
              ☰
            </button>
          </div>
        </div>

        {/* Mobile Nav (يظهر دون XL = أقل من 1280px) */}
        {mobileNavOpen && (
          <div className="xl:hidden border-t border-gray-100 bg-white max-h-[80vh] overflow-y-auto">
            <div className="px-4 py-2 flex flex-col gap-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  onClick={() => setMobileNavOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      'px-4 py-3 rounded-lg text-sm font-bold transition',
                      isActive ? 'text-navy-dark bg-blue-50' : 'text-gray-600 hover:bg-gray-50',
                    )
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              {session?.user && (
                <NavLink
                  to="/dashboard"
                  onClick={() => setMobileNavOpen(false)}
                  className="px-4 py-3 rounded-lg text-sm font-bold bg-navy-dark text-white text-center mt-2"
                >
                  🎯 {t('nav.dashboard')}
                </NavLink>
              )}
              {!session?.user && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <NavLink
                    to="/login"
                    onClick={() => setMobileNavOpen(false)}
                    className="px-4 py-3 rounded-lg text-sm font-bold bg-gray-100 text-navy-dark text-center"
                  >
                    {t('nav.login')}
                  </NavLink>
                  <NavLink
                    to="/register"
                    onClick={() => setMobileNavOpen(false)}
                    className="px-4 py-3 rounded-lg text-sm font-bold bg-gold text-white text-center"
                  >
                    {t('nav.register')}
                  </NavLink>
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      {/* 📄 Main — padding سفلي على الموبايل عشان bottom nav (محسوب مع safe-area iOS) */}
      <main className="flex-1 pb-[calc(72px+env(safe-area-inset-bottom))] md:pb-0">
        <Outlet />
      </main>

      {/* 🗺️ Map Floating Button — فوق زر الواتساب (سطح المكتب فقط) */}
      <Link
        to="/map"
        title="خريطة العقارات"
        aria-label="خريطة العقارات"
        className="hidden md:flex fixed bottom-24 left-6 z-30 w-14 h-14 rounded-full bg-navy-dark hover:bg-navy text-white items-center justify-center shadow-2xl hover:scale-110 transition-transform"
        style={{ boxShadow: '0 8px 24px rgba(30, 58, 95, 0.5)' }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          width="28"
          height="28"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
          <line x1="8" y1="2" x2="8" y2="18" />
          <line x1="16" y1="6" x2="16" y2="22" />
        </svg>
        {/* تسمية تظهر عند المرور */}
        <span className="absolute right-full mr-3 px-2.5 py-1 rounded-lg bg-navy-dark text-white text-xs font-bold whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition shadow-lg hidden md:inline-block">
          الخريطة
        </span>
      </Link>

      {/* WhatsApp Floating Button — متوفر في كل صفحات الموقع العام */}
      <a
        href="https://wa.me/966114993666?text=مرحباً، أبحث عن استشارة عقارية"
        target="_blank"
        rel="noopener noreferrer"
        title="تواصل واتساب"
        aria-label="تواصل عبر واتساب"
        className="fixed bottom-24 md:bottom-6 left-6 z-30 w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#25D366] hover:bg-[#1DA851] text-white flex items-center justify-center shadow-2xl hover:scale-110 transition-transform"
        style={{ boxShadow: '0 8px 24px rgba(37, 211, 102, 0.5)' }}
      >
        {/* Real WhatsApp logo (official glyph) */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 32 32"
          width="28"
          height="28"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.506 3.41 4.554 4.34.616.287 2.035.888 2.722.888.817 0 2.42-.516 2.762-1.32.144-.33.245-.673.245-1.032 0-.388-1.318-.871-1.586-.998-.318-.13-.633-.245-.93-.245zM16.026 32c-3.158 0-6.103-.873-8.626-2.522l-6.04 1.913 1.962-5.84A15.86 15.86 0 0 1 .066 15.974C.066 7.16 7.218 0 16.026 0c4.288 0 8.318 1.674 11.347 4.706a15.882 15.882 0 0 1 4.692 11.268c-.013 8.815-7.18 16.026-15.987 16.026h-.052zM16.045 2.706c-7.31 0-13.26 5.95-13.26 13.27 0 2.806.872 5.495 2.522 7.78l-1.157 3.4 3.534-1.13a13.215 13.215 0 0 0 7.385 2.247h.018c7.305 0 13.318-5.95 13.318-13.272a13.183 13.183 0 0 0-3.882-9.395c-2.51-2.515-5.85-3.9-9.394-3.9z"/>
        </svg>
        <span className="absolute -top-1 -right-1 flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
        </span>
      </a>

      {/* 📱 Bottom Nav للموبايل */}
      <MobileBottomNav />

      {/* ━━━━━━━━━━━━━━━━━━ FOOTER — تصميم نظيف بلون واحد ━━━━━━━━━━━━━━━━━━ */}
      <footer className="bg-[#0E1014] text-gray-400 mt-auto" dir="rtl">
        <div className="max-w-7xl mx-auto px-6 pt-14 pb-6">

          {/* السطر الأول: الأعمدة */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-10 pb-10 border-b border-white/[0.06]">
            {/* العمود 1 — الهوية */}
            <div className="col-span-2 md:col-span-1">
              <div className="text-white font-black text-xl mb-1 tracking-tight">
                {t('app.name')}
              </div>
              {/* 🏛️ الشعار الرسمي — مهم لـ SEO + لانطباع العلامة */}
              <div className="text-gold-light font-bold text-sm mb-3 tracking-wide">
                العقار بصورة جديدة
              </div>
              <p className="text-[13px] leading-relaxed text-gray-500 max-w-[260px]">
                {t('app.tagline')}
              </p>
            </div>

            {/* العمود 2 — روابط */}
            <FooterCol title={t('footer.quick_links')}>
              <FooterLink to="/properties">{t('nav.properties')}</FooterLink>
              <FooterLink to="/auctions">{t('nav.auctions')}</FooterLink>
              <FooterLink to="/brokers">{t('nav.brokers')}</FooterLink>
              <FooterLink to="/calculators">{t('nav.calculators')}</FooterLink>
            </FooterCol>

            {/* العمود 3 — قانوني */}
            <FooterCol title={t('footer.legal')}>
              <FooterLink to="/compliance">الامتثال للأنظمة</FooterLink>
              <FooterLink to="/terms">{t('footer.terms')}</FooterLink>
              <FooterLink to="/privacy">{t('footer.privacy')}</FooterLink>
              <FooterLink to="/copyright">حقوق الملكية</FooterLink>
              <FooterLink to="/credentials">وثائقنا وشهاداتنا</FooterLink>
              <FooterLink to="/about">من نحن</FooterLink>
            </FooterCol>

            {/* العمود 4 — تواصل */}
            <FooterCol title={t('footer.contact_us')}>
              <FooterLink to="/complaints">📣 تقديم شكوى</FooterLink>
              <li>
                <a href="tel:0114993666" dir="ltr" className="text-gray-400 hover:text-white transition block text-end">
                  0114993666
                </a>
              </li>
              <li>
                <a href="mailto:info@img.sa" dir="ltr" className="text-gray-400 hover:text-white transition block text-end">
                  info@img.sa
                </a>
              </li>
              <li className="text-gray-500 text-[13px]">
                الرياض، المملكة العربية السعودية
              </li>
            </FooterCol>
          </div>

          {/* السطر الثاني: شارة الامتثال (مدمجة بهدوء) */}
          <Link
            to="/compliance"
            className="flex items-center justify-center gap-3 py-6 text-[13px] text-gray-500 hover:text-gray-300 transition group"
          >
            <ShieldGlyph />
            <span>منصة معتمَدة وملتزمة بأنظمة الهيئة العامة للعقار</span>
            <span className="text-gray-600 group-hover:text-gray-400 transition">←</span>
          </Link>

          {/* السطر الأخير: حقوق + RamhCredit */}
          <div className="pt-6 border-t border-white/[0.06] flex flex-col md:flex-row gap-3 items-center justify-between text-[12px] text-gray-600">
            <span>© {new Date().getFullYear()} {t('app.name')} — العقار بصورة جديدة — {t('footer.rights')}</span>
            <RamhCredit inverted />
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ━━━━━━━━━━━━━━━━━━ Footer Subcomponents (تصميم نظيف) ━━━━━━━━━━━━━━━━━━ */
function FooterCol({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-white text-[13px] font-bold mb-4 tracking-wide">{title}</div>
      <ul className="space-y-3 text-[13px]">{children}</ul>
    </div>
  );
}

function FooterLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <li>
      <NavLink
        to={to}
        className="text-gray-400 hover:text-white transition"
      >
        {children}
      </NavLink>
    </li>
  );
}

/* أيقونة درع مبسّطة كـ SVG (بدون إيموجي ملوّن) */
function ShieldGlyph() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}
